import { memo } from 'react'
import { createLayerComponent, LayerProps } from '@react-leaflet/core'
import L, {
  VectorGridOptions,
  VectorTileLayerMouseEvent,
  VectorGrid as LeafletVectorGrid,
  PathOptions,
  VectorTileRenderer,
  Point,
  LatLngTuple,
  VectorTileLayerMouseEventHandler,
} from 'leaflet'
import { Feature, Polygon, MultiPolygon, GeoJsonProperties } from 'geojson'
import union from '@turf/union'
import { polygon } from '@turf/helpers'

import 'leaflet.vectorgrid/src/Leaflet.VectorGrid.js'
import 'leaflet.vectorgrid/src/Leaflet.VectorGrid.Protobuf.js'
import 'leaflet.vectorgrid/src/Leaflet.Renderer.Canvas.Tile.js'
import 'leaflet.vectorgrid/src/Leaflet.Renderer.SVG.Tile.js'

type VectorGridFeature = Feature<Polygon | MultiPolygon>
export interface Props extends LayerProps {
  url: string
  options?: Partial<VectorGridOptions>
  onClick?: VectorTileLayerMouseEventHandler
  filter?: VectorGridOptions['filter']
  activeStyles?: PathOptions
  onFeatureLoad?: ((features: VectorGridFeature[]) => void) | null
}

const defaultOptions: VectorGridOptions = {
  rendererFactory: L.svg.tile,
  subdomains: 'abcd',
  interactive: true,
  getFeatureId: ({ properties }) => properties.id,
}

export const VectorGrid = memo(
  createLayerComponent<LeafletVectorGrid, Props>(
    ({ url, options, onClick, onFeatureLoad, filter, activeStyles = {} }, context) => {
      const protobufOptions = { ...defaultOptions, ...options }
      const instance = L.vectorGrid.protobuf(url, protobufOptions)
      const getFeatureId = protobufOptions.getFeatureId!

      instance.on('mouseover', ({ propagatedFrom }: VectorTileLayerMouseEvent) => {
        const defaultStyle = options?.vectorTileLayerStyles?.[instance.getDataLayerNames()[0]]

        instance.setFeatureStyle(getFeatureId(propagatedFrom), {
          ...(typeof defaultStyle === 'function'
            ? defaultStyle(propagatedFrom.properties, context.map.getZoom())
            : defaultStyle),
          fillOpacity: 0.6,
          opacity: 0.8,
          ...activeStyles,
        })
      })

      instance.on('mouseout', ({ propagatedFrom }: VectorTileLayerMouseEvent) =>
        instance.resetFeatureStyle(getFeatureId(propagatedFrom))
      )

      if (onClick) {
        instance.on('click', onClick)
      }

      if (filter) {
        instance.setFilter(filter)
      }

      if (onFeatureLoad) {
        instance.on('load', (event) => {
          const map = context.map
          const zoom = map.getZoom().toString()
          const tiles = Object.entries(event.target._vectorTiles)
            .filter(([key]) => {
              const [, , z] = key.split(':')

              return z === zoom
            })
            .map(([, value]) => value) as VectorTileRenderer[]

          const featureMap = tiles.reduce((acc, vectorTile) => {
            Object.entries(vectorTile._features).forEach(([id, features]) => {
              acc[id] ??= {
                properties: features[0].feature.properties,
                points: [],
              }

              const points = features.map(({ feature }) =>
                feature._parts.map((part) =>
                  part.map(({ x, y }) => {
                    const origin = map.latLngToLayerPoint(
                      event.target._tileCoordsToNwSe(vectorTile._tileCoord)[0]
                    )
                    const latLng = map.layerPointToLatLng(new Point(x + origin.x, y + origin.y))

                    return [latLng.lng, latLng.lat] as LatLngTuple
                  })
                )
              )

              acc[id].points = acc[id].points.concat(points)
            })

            return acc
          }, {} as Record<string, { properties: GeoJsonProperties; points: LatLngTuple[][][] }>)

          onFeatureLoad(
            Object.values(featureMap).map(({ properties, points }) => ({
              ...points
                .map((_points) => polygon(_points))
                .reduce(
                  (_unionPolygon, _polygon) => union(_unionPolygon, _polygon) as Feature<Polygon>
                ),
              properties,
            }))
          )
        })
      }

      return { instance, context }
    },
    (instance, { filter, onClick, url }) => {
      if (onClick) {
        instance.on('click', onClick)
      }

      if (filter) {
        instance.setFilter(filter)
      }

      if (url) {
        instance.setUrl(url)
      }
    }
  )
)
